import { useState } from 'react';
import { DashboardLayout } from '@/components/layout';
import { ReceiptUploader, UnassignedReceiptsList } from '../components';
import { useProjectOptions } from '@/hooks';

type Tab = 'upload' | 'unassigned';

export function ReceiptsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('upload');
  const { projects } = useProjectOptions(['planned', 'in_progress']);

  const projectOptions = projects.map((p) => ({
    id: p.id,
    name: `${p.name}${p.customerName ? ` - ${p.customerName}` : ''}`,
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Receipts</h1>
          <p className="text-secondary-600 mt-1">
            Upload receipts and manage expense documentation
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-secondary-200">
          <nav className="-mb-px flex gap-6">
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'upload'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                Upload Receipts
              </span>
            </button>

            <button
              onClick={() => setActiveTab('unassigned')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'unassigned'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                Pending Assignment
              </span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl border border-secondary-200 p-6">
          {activeTab === 'upload' && (
            <ReceiptUploader
              projects={projectOptions}
              onComplete={() => {
                // Optionally switch to unassigned tab after upload
              }}
            />
          )}

          {activeTab === 'unassigned' && <UnassignedReceiptsList />}
        </div>

        {/* Help Text */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-primary-900 flex items-center gap-2">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Receipt Processing
          </h3>
          <p className="text-sm text-primary-700 mt-1">
            Upload receipts as images or PDFs. You can assign them to projects
            and suppliers.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}